<template>
  <div
    class="word-cloud-sphere"
    ref="container"
    :style="{ width: containerWidth + 'px', height: containerHeight + 'px' }"
    @mousedown="onDragStart"
    @mousemove="onDragMove"
    @mouseup="onDragEnd"
    @mouseleave="onDragEnd"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onDragEnd"
  >
    <div class="sphere-stage" ref="stage">
      <a
        v-for="(tag, index) in displayTags"
        :key="index"
        :href="tag.link || undefined"
        :target="tag.link ? '_blank' : undefined"
        :rel="tag.link ? 'noopener' : undefined"
        class="word-tag"
        :class="{ 'has-link': tag.link }"
        :style="tag.style"
        :ref="'tag-' + index"
        @click.stop="onTagClick($event, tag)"
      >{{ tag.text }}</a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WordCloud3D',
  props: {
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
    radius: { type: Number, default: 0 },
    fontScale: { type: Number, default: 1 },
    speed: { type: Number, default: 1.2 }
  },
  data () {
    return {
      // rotation state (radians)
      rotateX: 0.3,
      rotateY: 0,
      // auto-rotation speed
      autoSpeedX: 0.002,
      autoSpeedY: 0.003,
      // drag state
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartRotateX: 0,
      dragStartRotateY: 0,
      // inertia
      velocityX: 0,
      velocityY: 0,
      lastMoveTime: 0,
      lastMoveX: 0,
      lastMoveY: 0,
      // data
      rawTags: [],
      basePositions: [],  // { text, link, sx, sy, sz, color } — fixed 3D sphere coords
      displayTags: [],    // { text, link, style } — updated each frame
      // color palette
      colorList: [
        '#e27027', '#cc7b2e', '#ad4331', '#88343b',
        '#d4902f', '#c7a736', '#8d7a3d', '#d9b134',
        '#e8873a', '#cf5c2f', '#b8452d', '#a0a0a0'
      ],
      // responsive
      containerWidth: 0,
      containerHeight: 0,
      sphereRadius: 0,
      // animation
      animFrameId: null,
      // cached refs to DOM elements for fast updates
      tagEls: []
    }
  },
  mounted () {
    this.updateContainerSize()
    window.addEventListener('resize', this.updateContainerSize)
    this.$nextTick(() => {
      if (this.rawTags.length > 0) {
        this.buildSphere()
      }
    })
    this.startAutoRotate()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateContainerSize)
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId)
    }
  },
  methods: {
    // ========== public API ==========
    setTags (tags = []) {
      this.rawTags = tags.map(t => {
        if (typeof t === 'string') {
          return { text: t, link: '' }
        }
        return { text: t.text || '', link: t.link || '' }
      })
      this.$nextTick(() => {
        this.buildSphere()
      })
    },

    // ========== sizing ==========
    updateContainerSize () {
      const el = this.$refs.container
      if (!el) return
      const parent = el.parentElement
      const parentW = parent ? parent.clientWidth : window.innerWidth
      const isMobile = parentW <= 768

      this.containerWidth = this.width > 0 ? this.width : Math.min(parentW - 20, 600)
      this.containerHeight = this.height > 0
        ? this.height
        : isMobile
          ? Math.min(this.containerWidth * 0.85, 420)
          : Math.min(this.containerWidth * 0.75, 450)
      this.sphereRadius = this.radius > 0
        ? this.radius
        : Math.min(this.containerWidth, this.containerHeight) * 0.38

      this.$nextTick(() => {
        if (this.rawTags.length > 0) {
          this.buildSphere()
        }
      })
    },

    // ========== build Fibonacci sphere ==========
    buildSphere () {
      const n = this.rawTags.length
      const R = this.sphereRadius
      const goldenRatio = (1 + Math.sqrt(5)) / 2

      this.basePositions = this.rawTags.map((raw, i) => {
        // Fibonacci sphere algorithm — evenly distributed points
        const y = 1 - (i / (n - 1 || 1)) * 2
        const radiusAtY = Math.sqrt(1 - y * y)
        const theta = 2 * Math.PI * i / goldenRatio

        return {
          text: raw.text,
          link: raw.link,
          sx: Math.cos(theta) * radiusAtY * R,
          sy: y * R,
          sz: Math.sin(theta) * radiusAtY * R,
          color: this.colorList[i % this.colorList.length]
        }
      })

      // init displayTags with empty styles (will be filled on first frame)
      this.displayTags = this.basePositions.map(bp => ({
        text: bp.text,
        link: bp.link,
        style: {}
      }))

      // cache DOM refs after Vue renders
      this.$nextTick(() => {
        this.cacheTagEls()
      })
    },

    cacheTagEls () {
      this.tagEls = []
      const stage = this.$refs.stage
      if (!stage) return
      const anchors = stage.querySelectorAll('.word-tag')
      this.tagEls = Array.from(anchors)
    },

    // ========== project 3D → 2D each frame ==========
    projectAll () {
      if (this.basePositions.length === 0) return
      if (this.tagEls.length !== this.basePositions.length) {
        this.cacheTagEls()
        if (this.tagEls.length === 0) return
      }

      const cx = this.containerWidth / 2
      const cy = this.containerHeight / 2
      const rx = this.rotateX
      const ry = this.rotateY
      const cosX = Math.cos(rx)
      const sinX = Math.sin(rx)
      const cosY = Math.cos(ry)
      const sinY = Math.sin(ry)
      const R = this.sphereRadius
      const baseFontSize = Math.max(14, this.containerWidth / 20) * this.fontScale

      for (let i = 0; i < this.basePositions.length; i++) {
        const bp = this.basePositions[i]
        const el = this.tagEls[i]
        if (!el) continue

        const { sx, sy, sz } = bp

        // rotate around Y axis
        const x1 = sx * cosY - sz * sinY
        const z1 = sz * cosY + sx * sinY

        // rotate around X axis
        const y1 = sy * cosX - z1 * sinX
        const z2 = z1 * cosX + sy * sinX

        // depth factor: 0=back, 1=front
        const t = (z2 + R) / (2 * R)
        const clamped = Math.max(0, Math.min(1, t))

        // 2D screen position
        const screenX = cx + x1
        const screenY = cy + y1

        // depth-based size
        const scale = 0.25 + clamped * 0.75
        const fontSize = baseFontSize * scale

        // depth-based opacity
        const opacity = 0.3 + clamped * 0.7

        // z-index for correct overlap
        const zIndex = Math.round(clamped * 100)

        el.style.left = screenX + 'px'
        el.style.top = screenY + 'px'
        el.style.fontSize = fontSize + 'px'
        el.style.opacity = opacity
        el.style.zIndex = zIndex
        el.style.color = bp.color
      }
    },

    // ========== animation loop ==========
    startAutoRotate () {
      const animate = () => {
        if (!this.isDragging) {
          if (Math.abs(this.velocityX) > 0.0001 || Math.abs(this.velocityY) > 0.0001) {
            this.rotateX += this.velocityX
            this.rotateY += this.velocityY
            this.velocityX *= 0.95
            this.velocityY *= 0.95
          } else {
            this.rotateX += this.autoSpeedX * this.speed
            this.rotateY += this.autoSpeedY * this.speed
          }
        }
        this.projectAll()
        this.animFrameId = requestAnimationFrame(animate)
      }
      this.animFrameId = requestAnimationFrame(animate)
    },

    // ========== drag handlers ==========
    onDragStart (e) {
      this.isDragging = true
      this.velocityX = 0
      this.velocityY = 0
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragStartRotateX = this.rotateX
      this.dragStartRotateY = this.rotateY
      this.lastMoveTime = Date.now()
      this.lastMoveX = e.clientX
      this.lastMoveY = e.clientY
    },

    onTouchStart (e) {
      if (e.touches.length === 1) {
        const t = e.touches[0]
        this.isDragging = true
        this.velocityX = 0
        this.velocityY = 0
        this.dragStartX = t.clientX
        this.dragStartY = t.clientY
        this.dragStartRotateX = this.rotateX
        this.dragStartRotateY = this.rotateY
        this.lastMoveTime = Date.now()
        this.lastMoveX = t.clientX
        this.lastMoveY = t.clientY
      }
    },

    onDragMove (e) {
      if (!this.isDragging) return
      this.applyDrag(e.clientX, e.clientY)
    },

    onTouchMove (e) {
      if (!this.isDragging || e.touches.length !== 1) return
      const t = e.touches[0]
      this.applyDrag(t.clientX, t.clientY)
    },

    applyDrag (clientX, clientY) {
      const sensitivity = 0.005
      const dx = clientX - this.dragStartX
      const dy = clientY - this.dragStartY
      this.rotateX = this.dragStartRotateX + dy * sensitivity
      this.rotateY = this.dragStartRotateY + dx * sensitivity

      const now = Date.now()
      const dt = now - this.lastMoveTime
      if (dt > 0) {
        this.velocityX = (clientY - this.lastMoveY) / dt * 0.05
        this.velocityY = (clientX - this.lastMoveX) / dt * 0.05
      }
      this.lastMoveTime = now
      this.lastMoveX = clientX
      this.lastMoveY = clientY
    },

    onDragEnd () {
      this.isDragging = false
    },

    // ========== tag click ==========
    onTagClick (e, tag) {
      if (!tag.link) {
        e.preventDefault()
        this.$emit('showOptionsAndResult', true, tag.text, '', '')
      }
    }
  }
}
</script>

<style scoped>
.word-cloud-sphere {
  position: relative;
  margin: 0 auto;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
  overflow: visible;
}

.word-cloud-sphere:active {
  cursor: grabbing;
}

.sphere-stage {
  position: relative;
  width: 100%;
  height: 100%;
}

.word-tag {
  position: absolute;
  font-weight: 550;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0,0,0,0.15);
  text-decoration: none;
  padding: 2px 6px;
  transform: translate(-50%, -50%);
  -webkit-tap-highlight-color: transparent;
  will-change: left, top, font-size, opacity;
}

.word-tag:hover {
  text-shadow: 0 2px 8px rgba(0,0,0,0.30);
  /* slightly enlarge on hover */
  filter: brightness(1.1);
}

.word-tag.has-link:hover {
  text-decoration: underline;
}

/* mobile */
@media (max-width: 768px) {
  .word-tag {
    text-shadow: 0 1px 2px rgba(0,0,0,0.12);
    padding: 1px 4px;
  }
}
</style>
